import React, { useMemo, useCallback } from 'react'
import { emptyObj } from 'utils/fp'
import useReactRouter from 'use-react-router'
import { propEq } from 'ramda'
import useParams from 'core/hooks/useParams'
import UserMultiSelect from 'k8s/components/common/UserMultiSelect'
import GroupMultiSelect from 'k8s/components/common/GroupMultiSelect'
import PresetField from 'core/components/PresetField'
import { makeStyles } from '@material-ui/styles'
import DocumentMeta from 'core/components/DocumentMeta'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { listRoleBindings, updateRoleBinding } from './new-actions'
import Theme from 'core/themes/model'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { roleBindingsSelector } from './selectors'
import { routes } from 'core/utils/routes'
import { IRbacRoleBindingSelector } from './model'
import ModalForm from 'core/elements/modal/ModalForm'
import usePluginRouter from 'core/hooks/usePluginRouter'

const useStyles = makeStyles<Theme>((theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: 16,
  },
}))

const defaultParams = {
  users: [],
  groups: [],
}

export default function UpdateRoleBindingPage() {
  const classes = useStyles({})
  const { match, history } = useReactRouter()
  const { currentPluginId } = usePluginRouter()
  const roleBindingId = match.params.id
  const clusterId = match.params.clusterId

  const { loading } = useListAction(listRoleBindings, {
    params: { clusterId },
    requiredParams: ['clusterId'],
  })
  const roleBindings = useSelectorWithParams(roleBindingsSelector, { clusterId })

  const roleBinding = useMemo(
    () => roleBindings.find(propEq('id', roleBindingId)) || (emptyObj as IRbacRoleBindingSelector),
    [roleBindings, roleBindingId],
  )

  const { getParamsUpdater } = useParams(defaultParams)

  const { update, updating, reset, error: errorUpdatingRoleBinding } = useUpdateAction(
    updateRoleBinding,
  )

  const submitForm = useCallback(
    async (params) => {
      const { success } = await update({ ...roleBinding, ...params })
      if (success) handleClose()
    },
    [roleBinding],
  )
  const handleClose = () => {
    reset()
    history.push(routes.rbac.roleBindings.path({ plugin: currentPluginId }))
  }

  return (
    <>
      <DocumentMeta title="Update Role Binding" bodyClasses={['form-view']} />

      <ModalForm
        route={routes.rbac.editRoleBindings}
        title="Edit Role Binding"
        onSubmit={submitForm}
        onClose={handleClose}
        loading={loading}
        submitting={updating}
        error={errorUpdatingRoleBinding}
        loadingMessage={loading ? 'Loading role binding...' : 'Submitting form...'}
        submitTitle="Update Role Binding"
      >
        <>
          <FormFieldSection title="Basic Details" step={1}>
            <PresetField label="Name" value={roleBinding.name} />
            <PresetField label="Cluster" value={roleBinding.clusterName} />
            <PresetField label="Namespace" value={roleBinding.namespace} />
            {roleBinding.roleRef && <PresetField label="Role" value={roleBinding.roleRef.name} />}
          </FormFieldSection>
          {roleBinding.users && (
            <FormFieldSection title="Assign Users to this Binding" step={2}>
              <UserMultiSelect
                id="users"
                tooltip="Select users to assign this role"
                onChange={getParamsUpdater('users')}
                initialValue={roleBinding.users}
              />
            </FormFieldSection>
          )}
          {roleBinding.groups && (
            <FormFieldSection title="Assign Groups to this Binding" step={3}>
              <GroupMultiSelect
                id="groups"
                tooltip="Select groups to assign this role"
                onChange={getParamsUpdater('groups')}
                initialValue={roleBinding.groups}
              />
            </FormFieldSection>
          )}
        </>
      </ModalForm>
    </>
  )
}
